import { getAsync, putAsync } from "../protect-api";
import config from "../../config";
import { apiVersion } from "../../shared/constants/common.constant";

import { useMutation, useQuery } from "react-query";
import type { ProblemDetails } from "../../shared/types/problem-details.type";
import type { UseMutationResult, UseQueryResult } from "react-query";
import type {
  PreferencesResponse,
  SanctionBodiesResponse,
  UpdatePreferencesPayload,
} from "./settings.types";
import type { SettingsValues } from "../../shared/types/common.type";

export const SANCTION_BODY_KEY = "sanctions-bodies";
export const PREFERENCES_KEY = "preferences";

const getSanctionBodies = async (): Promise<SanctionBodiesResponse> => {
  const { data: sanctionBodiesResult } = await getAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/lookup/sanctionBodies`
  );

  return {
    sanctionBodies: sanctionBodiesResult.data ?? [],
  };
};

export const useGetSanctionBodies = (): UseQueryResult<
  SanctionBodiesResponse,
  ProblemDetails
> =>
  useQuery<SanctionBodiesResponse, ProblemDetails>([SANCTION_BODY_KEY], () =>
    getSanctionBodies()
  );

export const getPreferences = async (
  searchType: string,
  searchActionType: string
): Promise<PreferencesResponse> => {
  const { data } = await getAsync<PreferencesResponse>(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml?searchType=${searchType}&searchActionType=${searchActionType}`
  );

  return {
    searchType: data.searchType,
    searchActionType: data.searchActionType,
    threshold: data.threshold,
    datasets: data.datasets ?? [],
    sanctionBodies: data.sanctionBodies ?? [],
    isEmailRequired: data.isEmailRequired,
    emailRecipients: data.emailRecipients ?? [],
    pepTiers: data.pepTiers ?? [],
    dobMatching: data.dobMatching,
  };
};

export const useGetPreferences = (
  searchType: string,
  searchActionType: string,
  enabled = true
): UseQueryResult<PreferencesResponse, ProblemDetails> =>
  useQuery<PreferencesResponse, ProblemDetails>(
    [PREFERENCES_KEY, searchType, searchActionType],
    () => getPreferences(searchType, searchActionType),
    { enabled }
  );

const updatePreferences = async (
  payload: UpdatePreferencesPayload
): Promise<SettingsValues> => {
  const response = await putAsync(
    `${config.COMPLIANCE_API_URL}/${apiVersion}/settings/preferences/aml`,
    {
      threshold: payload.threshold,
      searchType: payload.searchType,
      searchActionType: payload.searchActionType,
      pepTiers: payload.pepTiers,
      datasets: payload.datasets,
      sanctionBodies: payload.sanctionBodies,
      isEmailRequired: payload.isEmailRequired,
      emailRecipients: payload.emailRecipients,
      dobMatching: payload.dobMatching,
    }
  );
  return response.data;
};

export const useUpdatePreferences = (): UseMutationResult<
  SettingsValues,
  ProblemDetails,
  UpdatePreferencesPayload
> =>
  useMutation<SettingsValues, ProblemDetails, UpdatePreferencesPayload>(
    updatePreferences
  );
